import { useEffect } from "react";
import Peer, { type DataConnection, type PeerError } from "peerjs";
import { useSessionContext } from "@/contexts/SessionContext";
import { teleconsultoriaPresencePeerId } from "./teleconsultoriaPeerId";

/**
 * Mantém o peer `tc-{cpf}` vivo só enquanto o painel de teleconsultoria
 * está montado e a disponibilidade está ligada.
 * Não troca mensagem: a conexão que chega é só o teste de presença do convite.
 */
export function useTeleconsultoriaPresencePublisher(active: boolean) {
  const { user } = useSessionContext();
  const username = user?.username;

  useEffect(() => {
    if (!active || typeof username !== "string" || !username) return;

    const peerId = teleconsultoriaPresencePeerId(username);
    if (!peerId) return;

    // true só depois do open desta aba. Outra aba com o mesmo ID não pode
    // ser destruída daqui: o destroy derrubaria a presença dela.
    let opened = false;
    let cancelled = false;
    const peer = new Peer(peerId);

    const closeWithoutPayload = (conn: DataConnection) => {
      // Espera o open para o picker marcar Online. Fechar antes disso
      // aborta a negociação e o convite veria o profissional como desconhecido.
      if (conn.open) {
        conn.close();
        return;
      }
      conn.once("open", () => {
        conn.close();
      });
    };

    peer.on("open", () => {
      if (cancelled) {
        peer.destroy();
        return;
      }
      opened = true;
    });

    peer.on("connection", (conn) => {
      closeWithoutPayload(conn);
    });

    peer.on("error", (err: PeerError<string>) => {
      if (err.type === "unavailable-id") {
        opened = false;
      }
    });

    return () => {
      cancelled = true;
      if (opened && !peer.destroyed) {
        peer.destroy();
      }
    };
  }, [active, username]);
}
